import type { ScenePlan } from 'js/src/data/html-design';

import { HTML_DESIGN_SKILL_META } from './skills/skillMeta';
import { documentUsesSkill } from './skills/skillRegistry';

/**
 * Checks an authored composition against the plan its design run had approved.
 */

const NON_RENDERED_TAGS = new Set([
    'SCRIPT',
    'STYLE',
    'TEMPLATE',
    'NOSCRIPT',
    'TITLE',
    'META',
    'LINK',
    'HEAD',
]);

function normalize(text: string): string {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

function words(text: string): string[] {
    const normalized = normalize(text);
    return normalized ? normalized.split(' ') : [];
}

function ownText(element: Element): string {
    let text = '';
    for (const node of Array.from(element.childNodes)) {
        if (node.nodeType === 3 /* TEXT_NODE */) {
            text += node.nodeValue ?? '';
        }
    }
    return text.replace(/\s+/g, ' ').trim();
}

interface TextBlock {
    own: string;
    readings: string[];
}

export function compositionTextBlocks(html: string): TextBlock[] {
    // A <br> is a word boundary on screen, but it leaves no character behind for the text nodes
    // either side of it to be separated on: "The age mix<br>is shifting" reads back as "The age
    // mixis shifting", a word no plan can contain, and the planned copy gets reported as unplanned.
    // Substituting whitespace before parsing keeps every reading below aligned with what the frame
    // actually shows.
    const document = new DOMParser().parseFromString(
        html.replace(/<br\s*\/?>/gi, ' '),
        'text/html',
    );
    const body = document.body;
    if (!body) {
        return [];
    }
    const flatten = (text: string | null): string => (text ?? '').replace(/\s+/g, ' ').trim();
    const blocks: TextBlock[] = [];
    for (const element of Array.from(body.querySelectorAll('*'))) {
        if (NON_RENDERED_TAGS.has(element.tagName)) {
            continue;
        }
        const own = ownText(element);
        if (!own) {
            continue;
        }
        const readings = [own, flatten(element.textContent)];
        for (let parent = element.parentElement; parent; parent = parent.parentElement) {
            readings.push(flatten(parent.textContent));
        }
        blocks.push({ own, readings: readings.filter(Boolean) });
    }
    return blocks;
}

function planCovers(reading: string, plannedWords: ReadonlySet<string>, planHasDigit: boolean) {
    const readingWords = words(reading);
    if (readingWords.length === 0) {
        return true;
    }
    if (readingWords.every((word) => /^[0-9]+$/.test(word))) {
        return planHasDigit;
    }
    return readingWords.every((word) => plannedWords.has(word));
}

export function findUnplannedText(html: string, plan: ScenePlan | null): string[] {
    if (!plan) {
        return [];
    }
    // No early return for an empty word set: a plan carrying no copy is a commitment that the graphic
    // shows none, so every block it renders is unplanned. A composition that genuinely has no text
    // yields no blocks below and passes anyway.
    const plannedWords = new Set((plan.text ?? []).flatMap(words));
    const planHasDigit = [...plannedWords].some((word) => /[0-9]/.test(word));

    const unplanned: string[] = [];
    const seen = new Set<string>();
    for (const { own, readings } of compositionTextBlocks(html)) {
        if (readings.some((reading) => planCovers(reading, plannedWords, planHasDigit))) {
            continue;
        }
        const key = normalize(own);
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        unplanned.push(own);
    }
    return unplanned;
}

export function findUnusedTechniques(html: string, techniques: readonly string[]): string[] {
    return techniques.filter((skill) => !documentUsesSkill(skill, html));
}

export function unusedTechniqueFinding(html: string, techniques: readonly string[]): string | null {
    const unused = findUnusedTechniques(html, techniques);
    if (unused.length === 0) {
        return null;
    }
    const listed = unused
        .map((skill) => {
            const summary = HTML_DESIGN_SKILL_META[skill]?.indexSummary;
            return summary ? `  - \`${skill}\` — ${summary}` : `  - \`${skill}\``;
        })
        .join('\n');
    return (
        `The composition does not implement ${unused.length} of the technique(s) this graphic was ` +
        `assigned:\n${listed}\n` +
        `Load the skill again and build what it actually describes — the markup it asks for and ` +
        `the timeline code that drives it. A technique you read and then replaced with an entrance ` +
        `of your own is the assignment not done, and it is why a graphic comes out flat. ` +
        `Re-planning the scene is not the fix: the assignment was made for the frame you described, ` +
        `so change the composition until it hosts the technique.`
    );
}

export function unplannedTextFinding(html: string, plan: ScenePlan | null): string | null {
    const unplanned = findUnplannedText(html, plan);
    if (unplanned.length === 0) {
        return null;
    }
    const quoted = unplanned.map((text) => `  - "${text}"`).join('\n');
    return (
        `The composition renders ${unplanned.length} piece(s) of text your approved plan does not ` +
        `contain:\n${quoted}\n` +
        `Delete them. You committed to what this graphic says when the plan was approved, and copy ` +
        `added after that is copy the graphic was not asked for — it is where a kicker, a caption ` +
        `restating the headline, or a second version of a figure gets in. If one of them genuinely ` +
        `belongs, it belongs in the plan: revise the plan, get it approved, and author to that.`
    );
}
